/**
 * Rename + delete actions for a pipeline stage row (muted icon buttons).
 */

import { Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/atoms/Button';
import { Tooltip } from '@/components/ui/tooltip';

export interface StageRowActionsProps {
  stageLabel: string;
  isDefault: boolean;
  canDelete: boolean;
  isEditing: boolean;
  isRenaming: boolean;
  onRename: () => void;
  onDelete?: () => void;
  className?: string;
}

export function StageRowActions({
  stageLabel,
  isDefault,
  canDelete,
  isEditing,
  isRenaming,
  onRename,
  onDelete,
  className,
}: StageRowActionsProps) {
  const { t } = useTranslation('job');

  const deleteTitle = canDelete
    ? t('stage_management.action_delete')
    : isDefault
      ? t('stage_management.delete_default_blocked')
      : t('stage_management.delete_not_empty');

  return (
    <div className={`flex shrink-0 items-center gap-1 ${className ?? ''}`}>
      <Tooltip content={t('stage_management.action_edit')} variant="top">
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<Pencil />}
          aria-label={t('stage_management.rename_aria', { name: stageLabel })}
          disabled={isEditing || isRenaming}
          onClick={onRename}
        />
      </Tooltip>
      <Tooltip content={deleteTitle} variant="top">
        <Button
          type="icon"
          intent="muted"
          size="sm"
          icon={<Trash2 />}
          aria-label={deleteTitle}
          disabled={!canDelete || !onDelete || isEditing || isRenaming}
          onClick={() => onDelete?.()}
        />
      </Tooltip>
    </div>
  );
}
